import { createColumnHelper } from '@tanstack/react-table';
import { CurrencyAmount } from '@uniswap/sdk-core';
import { POOLS_WITH_EMISSIONS } from 'constants/pools';
import JSBI from 'jsbi';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { transformPoolData } from './utils';
import { NumberType, useFormatter } from 'utils/formatNumbers';
import { getCorrectPoolChainId } from 'utils/getCorrectPoolChainId';
import { sortPoolBySymbol } from 'utils/helpers/sorting';

import useClientSidePagination from 'components/ui/Table/hooks/useClientSidePagination';
import useClientSideSorting from 'components/ui/Table/hooks/useClientSideSorting';
import {
  useCosmicPoolIds,
  usePoolDatas,
  useTopPools
} from 'graphql/api/pools/pools.hooks';
import { useMyPositionIds } from 'graphql/api/positions/positions.hooks';
import { useTokensFromMap } from 'hooks/Tokens';
import { useIsLoading } from 'hooks/useIsLoading';
import { usePlatformToken } from 'hooks/usePlatformToken';
import { useUSDPrice } from 'hooks/useUSDPrice';
import { useWeb3React } from 'hooks/useWeb3React';
import { useCombinedActiveList } from 'state/lists/hooks';

import { Badge } from 'components/ui/Badge';
import Table from 'components/ui/Table';
import { TableVariant } from 'components/ui/Table/constants';
import { Tooltip, TooltipContent, TooltipTrigger } from 'components/ui/Tooltip';

import {
  /* SateliteIcon, */
  StarIcon
} from 'components/Icons';
import { AssetLogoVariant } from 'components/Logo/AssetLogo';
import DoubleTokenLogo from 'components/composed/Logo/DoubleTokenLogo';
import type { TokenLogoData } from 'components/composed/Logo/Logo.types';

const APRHeader = () => {
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);

  const handleTooltipToggle = () => {
    setIsTooltipOpen((prev) => !prev);
  };

  return (
    <Tooltip
      delayDuration={0}
      open={isTooltipOpen}
      onOpenChange={setIsTooltipOpen}
    >
      <TooltipTrigger asChild>
        <span onClick={handleTooltipToggle}>APR</span>
      </TooltipTrigger>
      <TooltipContent className='max-w-60 text-wrap bg-blue-700' side='bottom'>
        Estimated annual returns are calculated based on weekly trading fees.
        APR may vary depending on trading volume and liquidity and does not
        account for potential market risks or fluctuations.
      </TooltipContent>
    </Tooltip>
  );
};

type PoolRow = {
  pool: {
    address: string;
    token0: TokenLogoData;
    token1: TokenLogoData;
    isCosmic?: boolean;
    hasMyPosition?: boolean;
    fee: number;
    tvl: number;
  };
  volume24h: number;
  volume7d: number;
  tvl: number;
  apr: number;
};

const AllPools = () => {
  const { chainId } = useWeb3React();

  const correctChainId = getCorrectPoolChainId({ chainId });

  const navigate = useNavigate();

  const { formatNumber } = useFormatter();

  const platformToken = usePlatformToken();

  const platformCurrency = CurrencyAmount.fromRawAmount(
    platformToken,
    JSBI.BigInt(10 ** platformToken.decimals)
  );

  const { data: platformTokenPrice } = useUSDPrice(platformCurrency);

  const columnHelper = createColumnHelper<PoolRow>();

  const columns = [
    columnHelper.accessor('pool', {
      header: 'Pool',
      cell: (info) => {
        const pool = info.cell.getValue();

        const poolWithEmission = POOLS_WITH_EMISSIONS.find(
          (poolWithEmission) => poolWithEmission.address === pool.address
        );
        const emissionsAPR =
          poolWithEmission && platformTokenPrice
            ? (poolWithEmission.emission * platformTokenPrice * 52 * 100) /
              pool.tvl
            : 0;

        return (
          <div className='flex flex-row items-center gap-1.5'>
            <DoubleTokenLogo
              chainId={correctChainId}
              token0={pool.token0}
              token1={pool.token1}
              variant={AssetLogoVariant.Secondary}
              showBorder
            />

            <span className='flex items-center text-xs'>
              {pool.token0.symbol}/{pool.token1.symbol}
            </span>

            <Tooltip delayDuration={0}>
              <TooltipTrigger>
                <Badge>{pool.fee}%</Badge>
              </TooltipTrigger>
              <TooltipContent
                className='z-modal max-w-60 text-wrap bg-blue-700'
                side='right'
              >
                Fee tier
              </TooltipContent>
            </Tooltip>

            {poolWithEmission && platformTokenPrice && (
              <Tooltip delayDuration={0}>
                <TooltipTrigger>
                  <Badge variant='green'>
                    {formatNumber({
                      input: emissionsAPR / 100,
                      type: NumberType.PoolAPR
                    })}
                  </Badge>
                </TooltipTrigger>
                <TooltipContent
                  className='z-modal max-w-60 text-wrap bg-blue-700'
                  side='right'
                >
                  Emissions APR
                </TooltipContent>
              </Tooltip>
            )}

            {(pool.isCosmic || pool.hasMyPosition) && (
              <div className='flex flex-row items-center gap-1'>
                {pool.hasMyPosition && (
                  <StarIcon className='size-3 stroke-blue-200' />
                )}

                {/* Cosmic reference */}
                {/* {pool.isCosmic && (
                  <SateliteIcon className='size-3 stroke-blue-200' />
                )} */}
              </div>
            )}
          </div>
        );
      },
      enableSorting: true,
      meta: {
        className: 'w-auto'
      }
    }),
    columnHelper.accessor('apr', {
      header: () => <APRHeader />,
      cell: (info) =>
        formatNumber({
          input: info.getValue() / 100,
          type: NumberType.PoolAPR
        })
    }),
    columnHelper.accessor('tvl', {
      header: 'TVL',
      cell: (info) =>
        formatNumber({
          input: info.getValue(),
          type: NumberType.PoolTVLCurrency
        }),
      enableSorting: true,
      meta: {
        className: 'text-center hidden lg:table-cell'
      }
    }),
    columnHelper.accessor('volume24h', {
      header: 'Vol. 24H',
      cell: (info) =>
        formatNumber({
          input: info.getValue(),
          type: NumberType.PoolVolume
        }),
      enableSorting: true,
      meta: {
        className: 'text-center hidden lg:table-cell'
      }
    })
  ];

  const { data: topPools, loading: loadingTopPools } =
    useTopPools(correctChainId);
  const topPoolIDs = useMemo(
    () => topPools?.pools?.map((pool: { id: string }) => pool.id) || [],
    [topPools]
  );
  const { data, loading: loadingPoolData } = usePoolDatas(
    topPoolIDs,
    correctChainId
  );

  const { data: cosmicPoolIds } = useCosmicPoolIds();
  const { data: myPositionIds } = useMyPositionIds();

  const isLoadingData = useIsLoading(loadingTopPools, loadingPoolData);

  const defaultListTokens = useCombinedActiveList();
  const tokensFromMap = useTokensFromMap(defaultListTokens, correctChainId);

  const tableData: PoolRow[] = useMemo(
    () =>
      transformPoolData({ data, cosmicPoolIds, myPositionIds, tokensFromMap }),
    [data, cosmicPoolIds, myPositionIds, tokensFromMap]
  );

  const { sortedList, ...sortConfig } = useClientSideSorting({
    data: tableData,
    customSort: {
      sortId: 'pool',
      sortFn: sortPoolBySymbol
    }
  });

  const { paginatedList, ...paginationConfig } =
    useClientSidePagination(sortedList);

  const memoizedPaginatedList = useMemo(() => paginatedList, [paginatedList]);

  return (
    <Table
      key='all-pools'
      data={memoizedPaginatedList}
      columns={columns}
      isLoading={isLoadingData}
      placeholder='No data found'
      showRowNumbers
      showPagination
      paginationConfig={paginationConfig}
      sortConfig={sortConfig}
      onRowClick={(row) => {
        navigate(`/pools/${row.pool.address}`);
      }}
      className='px-1'
      variant={TableVariant.Secondary}
      narrowColumns
    />
  );
};

export default AllPools;
